import type { ReactElement } from "react";
import { useState } from "react";

import { every, isEmpty } from "lodash";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import NotificationBanner from "app/pages/sales-intelligence/common-components/NotificationBanner/NotificationBanner";
import SignalTimeframeSelect from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalTimeframeSelect/SignalTimeframeSelect";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import SignalFiltersViewHeader from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersViewHeader/SignalFiltersViewHeader";
import SignalFiltersWrapper from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersWrapper/SignalFiltersWrapper";
import { StyledDropdownLabelGapWrapped } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import CompetitorRenewalComingUpView from "app/pages/sales-intelligence/sub-modules/signals/components/views/TechnologySignalFiltersView/CompetitorRenewalComingUpView/CompetitorRenewalComingUpView";
import type { TechnologySignalFilterViewProps } from "app/pages/sales-intelligence/sub-modules/signals/components/views/TechnologySignalFiltersView/models/TechnologySignalFilterViewProps";
import { StyledNotificationBannerWrapper } from "app/pages/sales-intelligence/sub-modules/signals/components/views/TechnologySignalFiltersView/styles";
import TechnologySignalDefaultFiltersView from "app/pages/sales-intelligence/sub-modules/signals/components/views/TechnologySignalFiltersView/TechnologySignalDefaultFiltersView/TechnologySignalDefaultFiltersView";
import SignalTechnologiesFilterTracked from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalTechnologiesFilter/SignalTechnologiesFilterTracked";
import type { SignalTimeframe } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";
import TechnologySignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalSubType";
import technologySignalTimeframeConfig from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalTimeframe";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { getTechnologiesFiltersValue } from "app/pages/sales-intelligence/sub-modules/signals/utils/technologies/filters";

import type { GroupedTechnologies } from "../../../../../modules/technology/types/technologies";
import useSignalFiltersState from "../../../hooks/useFiltersState";
import type TechnologySignalFilters from "../../../models/technology/TechnologySignalFilters";
import {
    StyledBannerText,
    StyledFiltersViewContent,
    StyledFiltersViewInner,
    StyledNotificationWrapper,
} from "../../styles";

type TechnologySignalFiltersViewProps = SignalFiltersViewProps<Partial<TechnologySignalFilters>> & {
    technologies: GroupedTechnologies;
};

const TechnologySignalFiltersView = (props: TechnologySignalFiltersViewProps) => {
    const {
        isActive,
        signalSubType,
        signalAlias,
        technologies,
        filters,
        renderSubmit,
        renderTemplateView,
        notificationView,
        signalTemplateHint,
    } = props;

    const { currentFilters, setCurrentFilters } = useSignalFiltersState(filters, props.onFiltersChange);
    const [selectedTimeframe, setSelectedTimeframe] = useState<SignalTimeframe | undefined>(() =>
        technologySignalTimeframeConfig.fromFilters(currentFilters),
    );

    const selectedValue = getTechnologiesFiltersValue(currentFilters);
    const isValueEmpty = every(selectedValue, isEmpty);
    const hasTimeframeSelect = signalSubType !== TechnologySignalSubType.CompetitorRenewalComingUp;

    function renderFilterWrapperView(viewProps: TechnologySignalFilterViewProps): ReactElement {
        switch (signalSubType) {
            case TechnologySignalSubType.CompetitorRenewalComingUp:
                return <CompetitorRenewalComingUpView {...viewProps} />;
            default:
                return <TechnologySignalDefaultFiltersView {...viewProps} />;
        }
    }

    function handleChange(updatedFilters: Partial<TechnologySignalFilters>) {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
    }

    function handleTimeframeSelect(timeframe: SignalTimeframe) {
        setSelectedTimeframe(timeframe);
        handleChange(technologySignalTimeframeConfig.toFilters(timeframe));
    }

    function handleSubmit() {
        props.onFiltersSubmit({ ...filters, ...currentFilters });
    }

    function onTemplateSelect(option: Readonly<SignalTemplate<TechnologySignalFilters>>) {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...option.filters }));
        setSelectedTimeframe(technologySignalTimeframeConfig.fromFilters(option.filters));
    }

    const isSubmitDisabled = isValueEmpty || (hasTimeframeSelect && !selectedTimeframe);

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <SignalFiltersViewHeader text={translate(signalsI18nKeys.managementLabel.technologyType)}>
                    {renderTemplateView({ onSelect: onTemplateSelect })}
                </SignalFiltersViewHeader>

                {isActive ? (
                    <>
                        <SignalFiltersWrapper>
                            {renderFilterWrapperView({
                                signalAlias,
                                signalSubType: signalSubType as TechnologySignalSubType | undefined,
                                selectedValue,
                                onValueChange: handleChange,
                                children: (
                                    <SignalTechnologiesFilterTracked
                                        filterKey="technologies"
                                        isValueEmpty={isValueEmpty}
                                        signalAlias={signalAlias}
                                        selectedValue={selectedValue}
                                        options={technologies}
                                        onValueChange={handleChange}
                                    />
                                ),
                            })}

                            {hasTimeframeSelect ? (
                                <>
                                    <StyledDropdownLabelGapWrapped>
                                        {translate(signalsI18nKeys.managementLabel.timeframe)}
                                    </StyledDropdownLabelGapWrapped>
                                    <SignalTimeframeSelect
                                        selectedTimeframe={selectedTimeframe}
                                        options={technologySignalTimeframeConfig.options}
                                        onSelect={handleTimeframeSelect}
                                    />
                                </>
                            ) : null}
                        </SignalFiltersWrapper>

                        {signalTemplateHint ? (
                            <StyledNotificationBannerWrapper>
                                <NotificationBanner notificationType="questions">
                                    <I18n
                                        data-automation="signal-hint-text"
                                        translatable={signalTemplateHint}
                                        element={StyledBannerText}
                                    />
                                </NotificationBanner>
                            </StyledNotificationBannerWrapper>
                        ) : null}
                    </>
                ) : null}

                {notificationView ? <StyledNotificationWrapper>{notificationView}</StyledNotificationWrapper> : null}
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {renderSubmit({
                    isDisabled: isSubmitDisabled,
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default TechnologySignalFiltersView;
